/**
 * DockShell — the resizable, foldable, full-screenable strip under the search answer.
 *
 * ─── WHY ONE SHELL ───
 *
 * The Search tab docks a time graph, the Track tab docks a route on a plan. They answer
 * different questions and draw nothing alike, but as FURNITURE they are the same object: a
 * titled strip under the answer that an operator folds away, drags taller, and now throws full
 * screen. Two copies of that meant the timeline had no drag at all and the path had no full
 * screen, which is not a design decision, it is what happens when chrome is written twice.
 *
 * The measurement below came from the path dock and is the hard-won part; see `measureCeiling`.
 *
 * ─── HEIGHT IS A NEGOTIATION, NOT A NUMBER ───
 *
 * The dock sits in a flex column with the answer above it. Every pixel it takes is a pixel the
 * answer loses, so it may not simply take what it was dragged to: it is bounded by what the
 * column can actually spare, re-measured whenever the page changes shape. A dock that ignores
 * that is a dock whose bottom edge goes off the page — which is exactly the bug this file's
 * ancestor had, reported as the canvas "hiding under the container".
 *
 * Full screen is the escape hatch from that negotiation: when an operator wants to READ the
 * route or the graph rather than glance at it, no amount of dragging in a 900px window will do,
 * and the answer above is not what they are looking at anyway.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { HidePaneButton } from './PaneControls';

/**
 * How tall a dock opens, and the bounds a drag may reach.
 *
 * A route needs room. The default is enough to read a floor plan on a laptop; below `MIN` the
 * captions under the pins collide and the panel stops being a map, and past `SHARE` of the
 * column the ANSWER it is describing is off screen — a dock that can eat the page is a dock
 * that gets closed rather than resized.
 */
export const DOCK_HEIGHT_DEFAULT = 320;
export const DOCK_HEIGHT_MIN = 160;

/**
 * The floor the MEASUREMENT may push a dock to, below the minimum a drag can reach.
 *
 * `MIN` is about the drag — a canvas smaller than that is not worth resizing to, so the handle
 * stops there. This is about something else: a page cramped enough that even 160px does not fit.
 * There, holding the minimum does not give the operator a bigger canvas, it pushes the bottom of
 * the one they have off the page, which is the whole defect.
 */
const HEIGHT_FLOOR = 96;

/** The share of its column a dock may take even when there is room for more. */
const HEIGHT_SHARE = 0.6;

/**
 * The first-paint guess, used ONLY before the dock has been laid out and measured.
 *
 * Everything above the dock — the app shell, the page header, the query bar, the filter bar
 * (which wraps), the status line — and the pager below it are chrome that cannot give any room
 * back. A guess at that total is wrong on any window whose filter bar wraps to another row or
 * whose shell differs between the local and cloud apps. So it is a guess for one frame and then
 * never again.
 */
const FIRST_PAINT_CHROME_PX = 430;

const heightMax = () => Math.max(
  DOCK_HEIGHT_MIN,
  Math.min(
    window.innerHeight - FIRST_PAINT_CHROME_PX,
    Math.round(window.innerHeight * HEIGHT_SHARE),
  ),
);

/**
 * The bound itself, as arithmetic — separated from the DOM so it can be asserted.
 * Exported for `scripts/test-tracking-path.ts`.
 */
export function ceilingFrom(room: {
  /** The flex column's own content height. */
  column: number;
  /** Rows that cannot give anything back. */
  fixed: number;
  /** What the flexible rows keep whatever the dock does. */
  floors: number;
  /** The dock's handle and header — everything in it that is not the canvas. */
  furniture: number;
  /** `window.innerHeight`, as the backstop below explains. */
  viewport: number;
}): number {
  return Math.max(
    HEIGHT_FLOOR,
    Math.min(
      room.column - room.fixed - room.floors - room.furniture,
      Math.round(room.column * HEIGHT_SHARE),
      // The backstop, for a host that hangs this page in a container with no definite height:
      // there the column grows WITH the dock, so a bound expressed only as a share of it would
      // chase itself upwards. The viewport cannot grow, so this one always settles.
      Math.round(room.viewport * HEIGHT_SHARE),
    ),
  );
}

/**
 * The tallest the dock's BODY may be, measured from the column the dock actually sits in.
 *
 * The column is a flex stack: some rows are fixed (`shrink-0`) and one — the answer — is
 * flexible and gives its room away as the dock grows. So the room available is the column, less
 * every fixed row, less the floor the flexible rows keep, less the dock's own furniture. Read
 * off the DOM rather than declared, because none of those are constants: the filter bar wraps
 * with the window, the pager comes and goes with the answer, and the two apps that share this
 * page hang it inside different shells.
 */
function measureCeiling(root: HTMLElement, body: HTMLElement | null): number {
  const parent = root.parentElement;
  if (!parent) return heightMax();

  const ps = getComputedStyle(parent);
  const column = parent.clientHeight
    - (parseFloat(ps.paddingTop) || 0) - (parseFloat(ps.paddingBottom) || 0);
  if (column <= 0) return heightMax();

  let fixed = 0;
  let floors = 0;
  for (const el of Array.from(parent.children)) {
    if (el === root || !(el instanceof HTMLElement)) continue;
    const s = getComputedStyle(el);
    // Out of flow, so it costs the column nothing — the record modal and the playback dock are
    // both `fixed`, and counting them would shrink the canvas whenever one is open.
    if (s.display === 'none' || s.position === 'fixed' || s.position === 'absolute') continue;
    if (parseFloat(s.flexGrow) > 0) floors += parseFloat(s.minHeight) || 0;
    else fixed += Math.max(el.getBoundingClientRect().height, el.scrollHeight);
  }

  // Measured as a difference so it stays right when the header wraps, and so it cannot depend
  // on the height this function is computing.
  const furniture = root.getBoundingClientRect().height
    - (body ? body.getBoundingClientRect().height : 0);

  return ceilingFrom({ column, fixed, floors, furniture, viewport: window.innerHeight });
}

/**
 * Remembered per dock, CLAMPED TO THIS WINDOW.
 *
 * The clamp is the point: a height dragged out on a 27-inch monitor is stored verbatim and read
 * back on a laptop, where it is taller than the page has room for. `localStorage` THROWS rather
 * than returning null in a private window and under some enterprise policies, so every access is
 * wrapped — a panel that failed to render because it could not remember its height would be a
 * far worse bug than one that forgets.
 */
function remembered(key: string, fallback: number, min: number): number {
  try {
    const raw = Number(localStorage.getItem(key));
    if (Number.isFinite(raw) && raw >= min) return Math.min(raw, heightMax());
  } catch { /* nothing was ever remembered, and nothing can be */ }
  return Math.min(fallback, heightMax());
}

function rememberedFlag(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch { /* as above */ }
  return fallback;
}

function store(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* as above */ }
}

export interface DockShellProps {
  /** Distinguishes one dock's remembered height and fold state from another's. */
  storageKey: string;
  title: string;
  /** Small print beside the title — what this dock is showing right now. */
  sub?: ReactNode;
  /** A figure worth reading without opening the dock (the peak bucket, the stops drawn). */
  meta?: ReactNode;
  /** Controls that belong to the CONTENT (a view toggle, a plan picker, a clear button). */
  actions?: ReactNode;
  defaultOpen?: boolean;
  defaultHeight?: number;
  minHeight?: number;
  /**
   * The body, given the height it has been granted. A plot that has to be drawn at a size takes
   * it from here rather than guessing, which is what lets the drag actually change the drawing
   * instead of just the box around it.
   */
  children: (bodyHeight: number, fullscreen: boolean) => ReactNode;
  /**
   * A PANE, not a dock: the shell fills its parent (a flex column) instead of negotiating a
   * height under an answer. No drag handle and no vertical fold — the Track page puts the path
   * in the middle column, where the height is the page's and folding is sideways.
   */
  fill?: boolean;
  /** Pane mode only: the header's fold button calls this (the parent collapses the column). */
  onCollapse?: () => void;
}

export default function DockShell({
  storageKey,
  title,
  sub,
  meta,
  actions,
  defaultOpen = true,
  defaultHeight = DOCK_HEIGHT_DEFAULT,
  minHeight = DOCK_HEIGHT_MIN,
  children,
  fill = false,
  onCollapse,
}: DockShellProps) {
  const heightKey = `trinetra.dock.${storageKey}.height`;
  const openKey = `trinetra.dock.${storageKey}.open`;

  const [open, setOpen] = useState(() => rememberedFlag(openKey, defaultOpen));
  const [height, setHeight] = useState(() => remembered(heightKey, defaultHeight, minHeight));
  const [dragging, setDragging] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [ceiling, setCeiling] = useState(() => heightMax());

  /**
   * The ceiling, re-measured whenever the page around the dock changes shape.
   *
   * WHAT IS WATCHED is the point. A window `resize` listener alone misses every other way the
   * column changes height — a filter chip wrapping the bar onto a third row, the pager appearing
   * under the dock, the search panel folding — and each of those is a few dozen pixels the dock
   * would go on claiming after they were spent.
   */
  useLayoutEffect(() => {
    // A pane takes the height the layout gives it; there is nothing to negotiate.
    if (fullscreen || fill) return undefined;
    const root = rootRef.current;
    const parent = root?.parentElement;
    if (!root || !parent) return undefined;

    // Sub-pixel churn is not a change worth a render, and re-rendering from inside a
    // ResizeObserver on every tick is how an observer loop starts.
    const remeasure = () => setCeiling((prev) => {
      const next = measureCeiling(root, bodyRef.current);
      return Math.abs(next - prev) < 1 ? prev : next;
    });
    remeasure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', remeasure);
      return () => window.removeEventListener('resize', remeasure);
    }

    const ro = new ResizeObserver(remeasure);
    const watch = () => {
      ro.disconnect();
      ro.observe(parent);
      for (const el of Array.from(parent.children)) ro.observe(el);
    };
    watch();
    const mo = new MutationObserver(() => { watch(); remeasure(); });
    mo.observe(parent, { childList: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [open, fullscreen, fill]);

  /** Escape leaves full screen — the same key that closes every other overlay in the product. */
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setFullscreen(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  /**
   * The resize drag.
   *
   * Pointer events with capture, not mouse events on `window`: capture keeps the handle
   * receiving moves when the cursor outruns it, which on a fast drag it always does, and it ends
   * the drag even if the pointer is released over a map that swallows mouse events of its own.
   *
   * The height is written on every move and remembered only at the END — writing through the
   * drag would put sixty entries a second into localStorage to keep the last one.
   */
  const dragFrom = useRef<{ y: number; h: number } | null>(null);
  const bodyHeight = Math.min(ceiling, Math.max(minHeight, height));

  const onResizeDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    // Stops the browser starting a text selection. The drag travels UP over the answer list, so
    // without this an operator who resizes the dock is left with half the page highlighted.
    e.preventDefault();
    if (!open) setOpen(true);
    dragFrom.current = { y: e.clientY, h: bodyHeight };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [bodyHeight, open]);

  const onResizeMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    if (!from) return;
    // Dragging UP makes it taller, so the delta is inverted: the handle is the panel's top edge
    // and the panel grows downwards from nothing.
    setHeight(Math.min(ceiling, Math.max(minHeight, from.h + (from.y - e.clientY))));
  }, [ceiling, minHeight]);

  const endResize = useCallback(() => {
    if (!dragFrom.current) return;
    dragFrom.current = null;
    setDragging(false);
    setHeight((h) => { store(heightKey, String(h)); return h; });
  }, [heightKey]);

  function toggleOpen() {
    setOpen((o) => { store(openKey, o ? '0' : '1'); return !o; });
  }

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-4 py-1.5">
      {fill && !fullscreen ? (
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
      ) : (
      <button
        type="button"
        onClick={fullscreen ? undefined : toggleOpen}
        aria-expanded={open || fullscreen}
        disabled={fullscreen}
        className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default disabled:hover:text-muted-foreground"
        title={fullscreen ? title : open ? `Hide ${title.toLowerCase()}` : `Show ${title.toLowerCase()}`}
      >
        {!fullscreen && (open
          ? <ChevronDownIcon className="h-3 w-3" />
          : <ChevronUpIcon className="h-3 w-3" />)}
        {title}
      </button>
      )}
      {sub && <span className="truncate text-[10px] text-muted-foreground">{sub}</span>}
      {meta && <span className="truncate text-[11px] tabular-nums text-muted-foreground">· {meta}</span>}

      <div className="ml-auto flex items-center gap-1.5">
        {/* Content controls stay live while folded only if they make sense folded; each dock
            decides by what it passes. */}
        {/* A pane is never folded, so its controls always show — the dock's remembered fold
            state (`open`) must not hide them. */}
        {(open || fullscreen || fill) && actions}
        <button
          type="button"
          onClick={() => { setFullscreen((f) => !f); if (!open) toggleOpen(); }}
          className="rounded-lg border border-border p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title={fullscreen ? 'Leave full screen (Esc)' : `${title} full screen`}
          aria-label={fullscreen ? 'Leave full screen' : 'Full screen'}
        >
          {fullscreen
            ? <ArrowsPointingInIcon className="h-3.5 w-3.5" />
            : <ArrowsPointingOutIcon className="h-3.5 w-3.5" />}
        </button>
        {fill && onCollapse && !fullscreen && (
          <HidePaneButton side="center" label={title.toLowerCase()} onClick={onCollapse} />
        )}
      </div>
    </div>
  );

  // ── Full screen: the dock becomes the page ──
  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-card">
        <div className="shrink-0 border-b border-border">{header}</div>
        {/* A flex COLUMN in both modes, so a body that fills (`flex-1 min-h-0`) gets a definite
            height from the layout rather than from a percentage that may not resolve — which is
            what a canvas has to measure itself against. */}
        <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* 0 means "take what you are given" — the body is a flex child here, not a fixed box. */}
          {children(0, true)}
        </div>
      </div>
    );
  }

  // ── Pane: fills the column it is given ──
  if (fill) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-card">
        {/* No rule under the header: the body draws its own top border. */}
        <div className="shrink-0">{header}</div>
        <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {children(0, false)}
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="shrink-0 border-t border-border bg-card">
      {/*
        The grab handle: the dock's top edge, four pixels of it, with a visible grip.
        `touch-none` so a drag on a touch screen resizes instead of scrolling the page behind.
      */}
      {open && (
        <div
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          role="separator"
          aria-orientation="horizontal"
          aria-label={`Resize ${title.toLowerCase()}`}
          tabIndex={0}
          onKeyDown={(e) => {
            // Keyboard resize, because a drag is not reachable without a pointer.
            const step = e.shiftKey ? 64 : 16;
            if (e.key === 'ArrowUp') { e.preventDefault(); setHeight((h) => Math.min(ceiling, h + step)); }
            if (e.key === 'ArrowDown') { e.preventDefault(); setHeight((h) => Math.max(minHeight, h - step)); }
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') store(heightKey, String(height));
          }}
          className={`group flex h-2 w-full cursor-row-resize touch-none items-center justify-center outline-none ${
            dragging ? 'bg-brand-light' : 'hover:bg-muted focus-visible:bg-brand-light'
          }`}
        >
          <span
            className={`h-0.5 w-8 rounded-full transition-colors ${
              dragging ? 'bg-brand' : 'bg-border group-hover:bg-muted-foreground'
            }`}
          />
        </div>
      )}

      {header}

      {open && (
        <div
          ref={bodyRef}
          style={{ height: bodyHeight }}
          className={`flex flex-col overflow-hidden ${dragging ? 'select-none' : ''}`}
        >
          {children(bodyHeight, false)}
        </div>
      )}
    </div>
  );
}
